import { boot } from 'quasar/wrappers'
import axios, { AxiosInstance } from 'axios'
import { runtimeConfig } from 'src/boot/runtime-config'
import { useApplicationEnvironmentStore } from 'src/stores/application-environment'
import { useNetworkStore } from 'stores/network'
import { useTestModeStore } from 'stores/test-mode'
import { DateTime } from 'luxon'
import { isOfflineRelatedError } from 'src/services/data/utils'
import Notifier from 'src/services/Notifier'
import ErrorHandler from 'src/services/ErrorHandler'
import { NetworkApiError } from 'src/models/error/errors'

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
let api: AxiosInstance
let healthCheck: AxiosInstance

export default boot(async ({ app }) => {
  const networkStore = useNetworkStore()

  healthCheck = axios.create({
    baseURL: runtimeConfig.HEALTHCHECK_URL,
    headers: { Accept: 'application/json' },
  })

  api = axios.create({
    baseURL: runtimeConfig.API_BASE_URL,
    headers: { Accept: 'application/json' },
  })

  api.interceptors.request.use((config) => {
    const applicationEnvironmentStore = useApplicationEnvironmentStore()
    const testModeStore = useTestModeStore()

    const controller = new AbortController()
    const newConfig = config

    if (applicationEnvironmentStore.applicationEnvironment?.reference) {
      newConfig.url = config.url?.replace(
        ':reference',
        encodeURIComponent(
          applicationEnvironmentStore.applicationEnvironment.reference,
        ),
      )
    }

    newConfig.timeout = 30000

    if (testModeStore.active && testModeStore.token) {
      newConfig.headers['X-test-mode-token'] = testModeStore.token
      newConfig.headers['X-test-mode-options'] =
        testModeStore.activeOptions.join(',')
    }

    if (networkStore.offlineMode) {
      controller.abort()
    }

    return {
      ...newConfig,
      signal: controller.signal,
    }
  })

  api.interceptors.response.use(
    (response) => {
      logLastOnlineDate()
      return response
    },
    (error) => {
      const err = ErrorHandler.convert(error)
      if (isOfflineRelatedError(err)) {
        networkStore.offlineMode = true
        Notifier.notifyError(new NetworkApiError())
      }

      return Promise.reject(error)
    },
  )

  healthCheck.interceptors.request.use((config) => {
    return {
      ...config,
      timeout: 30000,
    }
  })

  healthCheck.interceptors.response.use(
    (response) => {
      networkStore.offlineMode = false
      return response
    },
    (error) => {
      networkStore.offlineMode = true
      return Promise.reject(error)
    },
  )

  function logLastOnlineDate() {
    networkStore.lastOnline = DateTime.now()
  }

  // for use inside Vue files (Options API) through this.$axios and this.$api

  app.config.globalProperties.$axios = axios
  // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
  //       so you won't necessarily have to import axios in each vue file

  app.config.globalProperties.$api = api
  // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
  //       so you can easily perform requests against your app's API
})

export { api, healthCheck }
